import { useMemo } from 'react';
import { AlertTriangle, TrendingUp, DollarSign, HelpCircle } from 'lucide-react';
import type { VerbatimRespondent } from '../../data/verbatimData';

function pickQuotes(list: VerbatimRespondent[], field: keyof VerbatimRespondent, max: number): string[] {
  const candidates = list
    .map(r => String(r[field] || '').trim())
    .filter(t => t.length > 15 && t.length < 300);
  const step = Math.max(1, Math.floor(candidates.length / max));
  const picked: string[] = [];
  for (let i = 0; i < candidates.length && picked.length < max; i += step) {
    picked.push(candidates[i]);
  }
  return picked;
}

function avgAge(list: VerbatimRespondent[]): number {
  const ages = list.map(r => r.age).filter(a => a > 0);
  return ages.length > 0 ? Math.round(ages.reduce((a, b) => a + b, 0) / ages.length) : 0;
}

function pctMatch(list: VerbatimRespondent[], pred: (r: VerbatimRespondent) => boolean): string {
  return list.length > 0 ? `${Math.round(list.filter(pred).length / list.length * 100)}%` : '0%';
}

function topGoal(list: VerbatimRespondent[]): string {
  const counts: Record<string, number> = {};
  for (const r of list) if (r.primaryGoal) counts[r.primaryGoal] = (counts[r.primaryGoal] || 0) + 1;
  const sorted = Object.entries(counts).sort((a, b) => b[1] - a[1]);
  if (!sorted[0]) return 'N/A';
  const goal = sorted[0][0];
  return goal.length > 30 ? goal.slice(0, 27) + '...' : goal;
}

/** Renders stat text with numbers in mono font */
function StatChip({ text }: { text: string }) {
  // Split text on number-like patterns (digits, $, %, #)
  const parts = text.split(/(\d[\d,.]*%?|\$[\d,.]+)/g);
  return (
    <span className="px-2 py-0.5 rounded" style={{ backgroundColor: 'var(--color-surface)' }}>
      {parts.map((part, i) =>
        /^\d|^\$/.test(part) ? <span key={i} className="font-mono font-medium text-primary">{part}</span> : <span key={i}>{part}</span>
      )}
    </span>
  );
}

interface CardProps {
  icon: React.ReactNode;
  borderColor: string;
  title: string;
  stat: string;
  quotes: string[];
  insight: string;
  extraStats?: string[];
}

function SpotlightCard({ icon, borderColor, title, stat, quotes, insight, extraStats }: CardProps) {
  return (
    <div className={`card border-l-4 ${borderColor}`}>
      <div className="flex items-start gap-3 mb-3">
        <div className="mt-0.5">{icon}</div>
        <div>
          <h4 className="text-sm font-semibold text-primary">{title}</h4>
          <p className="text-sm text-muted">{stat}</p>
        </div>
      </div>
      <div className="space-y-2 mb-3">
        {quotes.map((q, i) => (
          <p key={i} className="text-sm text-secondary border-l-2 border-border pl-2 italic leading-relaxed">
            "{q}"
          </p>
        ))}
      </div>
      {extraStats && extraStats.length > 0 && (
        <div className="flex flex-wrap gap-x-4 gap-y-1 mb-2 text-micro text-secondary">
          {extraStats.map((s, i) => <StatChip key={i} text={s} />)}
        </div>
      )}
      <p className="text-detail text-muted">{insight}</p>
    </div>
  );
}

interface Props {
  data: VerbatimRespondent[];
}

export function SpotlightCards({ data }: Props) {
  const cards = useMemo(() => {
    const alwaysNone = data.filter(r => r.segment === 'always-none');
    const neverNone = data.filter(r => r.segment === 'never-none');
    const budgetCrowd = data.filter(r => {
      if (r.segment === 'always-none') return false;
      const low = r.priceDistribution['499'] || 0;
      const total = Object.values(r.priceDistribution).reduce((a, b) => a + b, 0);
      return total > 0 && low / total > 0.5;
    });

    const brandNever = data.filter(r =>
      /nothing|never|no idea|don't know|not sure|idk|no clue|haven't heard/i.test(r.brandPerception)
    );

    const spendNothing = (list: VerbatimRespondent[]) =>
      pctMatch(list, r => r.monthlySpend.toLowerCase().includes('nothing'));
    const spend50Plus = (list: VerbatimRespondent[]) =>
      pctMatch(list, r => /\$50|\$100|\$200/i.test(r.monthlySpend));
    const seekRarely = (list: VerbatimRespondent[]) =>
      pctMatch(list, r => /rarely|never/i.test(r.seekFrequency));

    return [
      {
        icon: <AlertTriangle size={16} className="text-red-500" />,
        borderColor: 'border-l-red-400',
        title: 'The 21.7% Who Said No to Everything',
        stat: `${alwaysNone.length} respondents chose "none" on all 15 tasks`,
        quotes: pickQuotes(alwaysNone, 'wtpFactors', 3),
        insight: `These respondents are unreachable at any tested configuration. They map to the "Low-Interest Prospects" segment in the conjoint.`,
        extraStats: [
          `Avg age: ${avgAge(alwaysNone)}`,
          `${spendNothing(alwaysNone)} spend nothing on wellness`,
          `Top goal: ${topGoal(alwaysNone)}`,
        ],
      },
      {
        icon: <TrendingUp size={16} className="text-teal-500" />,
        borderColor: 'border-l-teal-400',
        title: 'What the Most Engaged Buyers Want',
        stat: `${neverNone.length} respondents always chose a product (0 none selections)`,
        quotes: pickQuotes(neverNone, 'goals', 3),
        insight: `Your highest-engagement respondents consistently chose products across all price points. Their goals reveal what drives cold-market demand.`,
        extraStats: [
          `Avg age: ${avgAge(neverNone)}`,
          `${spend50Plus(neverNone)} already spend $50+/mo`,
          `Top goal: ${topGoal(neverNone)}`,
        ],
      },
      {
        icon: <DollarSign size={16} className="text-amber-500" />,
        borderColor: 'border-l-amber-400',
        title: 'The $499 Crowd — In Their Own Words',
        stat: `${budgetCrowd.length} respondents primarily chose $499 concepts`,
        quotes: pickQuotes(budgetCrowd, 'wtpFactors', 3),
        insight: `Budget seekers show 57.6% demand at $499 but crash above $999. Their WTP factors reveal what it would take to move them upmarket.`,
        extraStats: [
          `Avg age: ${avgAge(budgetCrowd)}`,
          `${spendNothing(budgetCrowd)} currently spend nothing`,
          `${pctMatch(budgetCrowd, r => r.gender === 'Female')} female`,
        ],
      },
      {
        icon: <HelpCircle size={16} className="text-blue-500" />,
        borderColor: 'border-l-blue-400',
        title: 'AlterMe Brand Awareness: Starting from Zero',
        stat: `${brandNever.length} of ${data.length} respondents had never heard of AlterMe`,
        quotes: pickQuotes(brandNever, 'brandPerception', 3),
        insight: `The conjoint measured cold-market demand from people with no brand awareness. This reinforces that these are top-of-funnel signals, not customer feedback.`,
        extraStats: [
          `${seekRarely(brandNever)} seek wellness solutions rarely or never`,
          `${spendNothing(brandNever)} spend nothing on wellness`,
        ],
      },
    ];
  }, [data]);

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
      {cards.map((c, i) => (
        <SpotlightCard key={i} {...c} />
      ))}
    </div>
  );
}
